import { useMemo, useEffect } from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  Asset,
  CreateAsset,
  UpdateAsset,
  getGetAssetQueryKey,
  getGetAssetsQueryKey,
  getGetTenantByIdQueryKey,
  getCreateAssetMutationOptions,
  getUpdateAssetMutationOptions,
  useCreateAsset as useApiClientCreateAsset,
  useUpdateAsset as useApiClientUpdateAsset,
  useGetAsset,
  useGetAssets,
  getAsset as getAssetFromApi,
  useGetAllPhotosByAsset,
  GetAssetsParams,
  CreateAssetResponse,
  getAssets,
  getAssetsFromWorkTaskIds,
  GetAssetsFromWorkTaskIdsRequest,
  GetAssetsFromWorkTaskIdsParams,
  GetAssetsFromWorkTaskIdsResponse,
} from 'api-client';
import TenantRepository from 'common/infrastructure/tenant/repository';
import {
  markSubmissionHostDone,
  failWorkSubmissionsForHost,
} from 'common/infrastructure/work-task/workSubmissionOutbox';
import FieldRepository from '../../field/repository';
import { AssetMapper } from './mapper';
import {
  MutationOptions,
  defaultMutationOptions,
} from 'common/infrastructure/remoteState/mutations.interface';
import AssetClassRepository from '../repository';
import { AxiosError } from 'axios';
import { queryClient as moduleQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { CreateAssetCommand } from '@planitgeo/treeplotter-domain/lib/modules/asset-class/asset/domain/command/create-asset.command';
import { UpdateAssetCommand } from '@planitgeo/treeplotter-domain/lib/modules/asset-class/asset/domain/command/update-asset.command';
import { AssetEntity } from '@planitgeo/treeplotter-domain/lib/modules/asset-class/asset/domain/asset.model';
import { FieldEntity } from '@planitgeo/treeplotter-domain/lib/modules/field/field/domain/field.model';
import { BoundingBox } from '@planitgeo/treeplotter-domain/lib/infrastructure/geo/geo.interface';
import { commandBus } from 'common/infrastructure/command.bus';
import { AssetWithTp2 } from './interface';
import { dispatch, store } from 'redux/store';
import {
  saveLastLoadedAsset,
  clearLastLoadedAsset,
  setLastAccessedAsset,
  setHasPendingPhotoUploads,
  setHasActiveCompressions,
  setCancelUploadsRequested,
} from './redux';
import { AssetAttributes } from '@planitgeo/treeplotter-domain/lib/modules/asset-class/asset/domain/asset.interface';
import { FieldTag } from '@planitgeo/treeplotter-domain/lib/modules/field/field/domain/field.interface';
import {
  isStemEligible,
  resolveStemFieldSet,
} from '@planitgeo/treeplotter-domain/lib/infrastructure/dbh/multi-stem-write';
import { FLAG_MULTI_STEM } from 'featureFlags/flags';
import { isFeatureFlagEnabled } from 'featureFlags/isFeatureFlagEnabled';
import PhotoRepository from '../assetPhotos/repository';
import {
  getPendingPhotoLinks,
  clearPendingPhotoLinks,
  recordEntityTp2Id,
} from 'common/infrastructure/photo-management/upload.cache';
import { VITE_ENV } from 'config';
import { Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';

type CreateMutationContext = {
  previousAssets?: Asset[];
};

const invalidFieldTagsForLoadLast = [
  FieldTag.LATITUDE,
  FieldTag.LONGITUDE,
  FieldTag.ARCHIVED,
  FieldTag.ASSET_CLASS_PRIMARY_IDENTIFIER,
  FieldTag.CREATED_BY,
  FieldTag.LAST_MODIFIED_USER,
  FieldTag.LAST_MODIFIED,
  FieldTag.DATE_CREATED,
  FieldTag.ADDRESS,
  // TPM-1536. Safe to exclude globally: a tenant without the calculator has no field
  // carrying either tag, so nothing changes for them. Stems are additionally excluded by
  // the Hidden check below, but naming them here keeps the intent from depending on how
  // the import happens to stamp inputType.
  FieldTag.MULTI_STEM_DBH,
  FieldTag.MULTI_STEM_TRIGGER,
];

/**
 * TPM-1536 / AC-12. The aggregate CANNOT go in the list above.
 *
 * While the stem editor is live it is a derived value — copying it forward without the
 * stems it was derived from would hand the next tree a DBH with no measurements behind it.
 * But on the ~255 tenants without the calculator, DBH is an ordinary measurement that Load
 * Last copies today, and a global exclusion would silently take that away — breaking the
 * epic's own premise that nobody loses anything they already have.
 *
 * "Live" is entitlement AND the flag, not entitlement alone: with the flag off the stem
 * editor never renders, the plain DBH input is the control the surveyor uses, and DBH is an
 * ordinary measurement on a provisioned tenant exactly as it is on an unprovisioned one.
 * Keying this on eligibility alone would take Load Last's DBH away from ~577 tenants for
 * the whole flag-off window.
 */
const shouldKeepField = (field: FieldEntity | undefined, stemEditorLive: boolean): boolean => {
  if (!field) return false;
  if (stemEditorLive && field.tag === FieldTag.DBH) return false;

  return (
    !invalidFieldTagsForLoadLast.includes(field.tag) &&
    field.inputType !== 'Hidden' &&
    !field.readOnly &&
    !field.immutable
  );
};

// Always returns a new array — in-place mutation of the cached list is
// invisible to react-query's reference-based change detection.
export const upsertAssetDto = (oldAssetDTOs: Asset[] | undefined, assetDto: Asset): Asset[] => {
  const next = [...(oldAssetDTOs || [])];
  const itemIndex = next.findIndex((item) => item.id === assetDto.id);

  if (itemIndex > -1) {
    next[itemIndex] = assetDto;
  } else {
    next.push(assetDto);
  }

  return next;
};

const AssetRepository = {
  useAssetPhotos(
    assetClassId: Tp3Id,
    assetId: Tp3Id,
    tp2AssetId?: number,
    options?: { enabled?: boolean }
  ) {
    const tenantId = TenantRepository.getCurrentTenantPid();

    // Build params object, only including tp2AssetId if it exists
    const params: {
      assetId: Tp3Id;
      assetClassId: Tp3Id;
      tp2AssetId?: number;
    } = {
      assetId,
      assetClassId,
    };

    if (tp2AssetId !== undefined) {
      params.tp2AssetId = tp2AssetId;
    }

    const useQueryResponse = useGetAllPhotosByAsset(tenantId, params, {
      query: { enabled: options?.enabled !== false },
    });
    const photos = useQueryResponse?.data;
    return { photos, refetch: useQueryResponse.refetch, isLoading: useQueryResponse.isLoading };
  },

  isBoundingBoxDefined(boundingBox?: BoundingBox): boolean {
    return (
      boundingBox?.minLng !== undefined &&
      boundingBox?.minLat !== undefined &&
      boundingBox?.maxLng !== undefined &&
      boundingBox?.maxLat !== undefined
    );
  },

  useAssets(
    assetClassId: Tp3Id,
    options: {
      boundingBox?: BoundingBox;
      tp2OrganizationId?: number;
      enabled?: boolean;
      dbhMin?: number;
      dbhMax?: number;
    } = {}
  ) {
    const { boundingBox, tp2OrganizationId, enabled = true, dbhMin, dbhMax } = options;
    const { fieldEntities } = FieldRepository.useGetFields(assetClassId);
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryParams = {
      assetClassId,
      tp2OrganizationId,
      minLng: boundingBox?.minLng,
      minLat: boundingBox?.minLat,
      maxLng: boundingBox?.maxLng,
      maxLat: boundingBox?.maxLat,
      limit: parseInt(VITE_ENV.assets.maxAssetsPerRequest),
      dbhMin,
      dbhMax,
    };

    const useQueryResponse = useGetAssets(
      tenantId,
      {
        ...queryParams,
      },

      {
        query: {
          enabled:
            enabled &&
            this.isBoundingBoxDefined(boundingBox) &&
            AssetClassRepository.getAssetFetchStatus(assetClassId),
          gcTime: 0,
        },
      }
    );

    const assetEntities = useMemo(() => {
      if (!useQueryResponse?.data || !Array.isArray(useQueryResponse.data)) {
        return [];
      }
      return useQueryResponse.data.map((asset: Asset) => AssetMapper.fromDTO(asset, fieldEntities));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(useQueryResponse?.data)]);

    // Clean up other queries that match the assetClassId
    useMemo(() => {
      if (!this.isBoundingBoxDefined(boundingBox)) return;

      moduleQueryClient.removeQueries({
        queryKey: getGetAssetsQueryKey(tenantId, { assetClassId }),
        exact: false,
        predicate: (query) => {
          const { queryKey } = query;
          const params = queryKey[5] as GetAssetsParams;

          return (
            params.tp2OrganizationId !== tp2OrganizationId ||
            params.minLng !== boundingBox?.minLng ||
            params.minLat !== boundingBox?.minLat ||
            params.maxLng !== boundingBox?.maxLng ||
            params.maxLat !== boundingBox?.maxLat
          );
        },
      });
    }, [assetClassId, boundingBox, tp2OrganizationId, tenantId]);

    const { tenant } = TenantRepository.useCurrentTenant();

    // Pure computation: check if there's a field configuration error
    const hasFieldConfigError = useMemo(() => {
      if (!useQueryResponse?.failureReason || !tenant?.id) {
        return false;
      }
      const { errCode, statusCode } = useQueryResponse.failureReason;
      return errCode === 'ERR_NOT_FOUND' || statusCode === 404 || statusCode === 500;
    }, [useQueryResponse?.failureReason, tenant?.id]);

    // Side effects: disable asset fetching and log error when field config error occurs
    useEffect(() => {
      if (hasFieldConfigError && tenant?.id && useQueryResponse?.failureReason) {
        AssetClassRepository.setAssetsFetch(false);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const failureReason = useQueryResponse.failureReason as any;
        loggerService.log(LoggerLevel.ERROR, 'Field configuration error detected', {
          assetClassId,
          errCode: failureReason.errCode,
          statusCode: failureReason.statusCode,
          tenant: tenant.id,
        });
      }
    }, [hasFieldConfigError, tenant?.id, assetClassId, useQueryResponse?.failureReason]);

    return {
      ...useQueryResponse,
      assetEntities,
      refetch: useQueryResponse.refetch,
      hasFieldConfigError,
    };
  },

  async prefetchAssets(
    assetClassId: Tp3Id,
    options: {
      boundingBox?: BoundingBox;
      tp2OrganizationId?: number | null;
    } = {}
  ) {
    const { boundingBox, tp2OrganizationId } = options;
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryParams = {
      assetClassId,
      tp2OrganizationId: tp2OrganizationId ?? undefined,
      minLng: boundingBox?.minLng,
      minLat: boundingBox?.minLat,
      maxLng: boundingBox?.maxLng,
      maxLat: boundingBox?.maxLat,
    };

    const queryKey = getGetAssetsQueryKey(tenantId, queryParams);
    await moduleQueryClient.prefetchQuery({
      queryKey,
      queryFn: () => getAssets(tenantId, queryParams),
    });
  },

  useCreateAsset() {
    return useApiClientCreateAsset({
      mutation: {
        ...AssetRepository.getCreateAssetMutationConfig(),
      },
    });
  },

  getCreateAssetMutationConfig(): MutationOptions {
    const { onMutate, onError, onSuccess, onSettled } =
      AssetRepository._createCallbacks(moduleQueryClient);

    return getCreateAssetMutationOptions({
      mutation: {
        ...defaultMutationOptions,
        onMutate,
        onSuccess,
        onError,
        onSettled,
        mutationKey: ['createAsset'],
      },
    }) as MutationOptions;
  },

  _createCallbacks(queryClient: QueryClient) {
    return {
      onMutate: async ({
        params: { assetClassId },
        data,
      }: {
        params: { assetClassId: Tp3Id };
        data: CreateAsset;
      }) => {
        commandBus.execute({
          payload: new CreateAssetCommand({
            externalId: data.id,
            assetClassId: assetClassId,
            attributes: data.attributes,
          }),
          type: 'CreateAssetCommand',
        });

        const currentTenantPid = TenantRepository.getCurrentTenantPid();
        await queryClient.cancelQueries({
          queryKey: getGetAssetsQueryKey(currentTenantPid, { assetClassId }),
          exact: false,
        });

        return {};
      },
      onSuccess: async (
        data: void | CreateAssetResponse,
        variables: { params: { assetClassId: Tp3Id }; data: CreateAsset }
      ): Promise<void> => {
        const tp2AssetIdAvailable = data && 'tp2AssetId' in data && data.tp2AssetId;

        // Unblock work queued against this new asset. The backend may rewrite the id, so
        // resolve the outbox entry (keyed by the minted id we submitted) onto the
        // canonical id + tp2 id it returns. The runner needs tp2AssetId to create work;
        // when it's assigned asynchronously (absent here) the entry waits, TTL-bounded.
        const submissionId = variables.data.id;
        if (submissionId) {
          markSubmissionHostDone(submissionId, {
            assetId: (data && 'id' in data && data.id) || submissionId,
            ...(tp2AssetIdAvailable ? { tp2AssetId: data.tp2AssetId } : {}),
          });
        }

        const canonicalAssetId = data && 'id' in data && data.id;
        if (
          canonicalAssetId &&
          canonicalAssetId !== variables.data.id &&
          AssetRepository.getLastAccessedAssetId() === variables.data.id
        ) {
          AssetRepository.setLastAccessedAsset(canonicalAssetId);
        }

        if (tp2AssetIdAvailable) {
          const { id: assetId } = variables.data;
          const { assetClassId } = variables.params;

          loggerService.log(
            LoggerLevel.DEBUG,
            `Asset created with tp2AssetId: ${data.tp2AssetId} for asset: ${assetId}`
          );

          await recordEntityTp2Id(assetId as Tp3Id, data.tp2AssetId);

          try {
            const pendingLinks = await getPendingPhotoLinks(assetId as Tp3Id);

            if (pendingLinks.length > 0) {
              loggerService.log(
                LoggerLevel.DEBUG,
                `Found ${pendingLinks.length} pending photo links for asset: ${assetId}`
              );

              const result = await PhotoRepository.linkPendingPhotosDirectly(
                assetId as Tp3Id,
                assetClassId,
                data.tp2AssetId
              );

              if (result.success) {
                loggerService.log(
                  LoggerLevel.DEBUG,
                  `Successfully linked ${result.linkedCount} photos to asset: ${assetId}`
                );
                await clearPendingPhotoLinks(assetId as Tp3Id);
              }
            } else {
              loggerService.log(
                LoggerLevel.DEBUG,
                `No pending photo links found for asset: ${assetId}`
              );
            }
          } catch (error) {
            loggerService.log(
              LoggerLevel.ERROR,
              `Failed to link photos for asset: ${assetId}`,
              error
            );
          }
        }
      },
      onError: (
        error: Error | AxiosError,
        variables: { params: { assetClassId: Tp3Id }; data: CreateAsset },
        _context: CreateMutationContext | undefined
      ): void => {
        loggerService.log(LoggerLevel.ERROR, 'Create asset error', {
          error: error instanceof Error ? error.message : String(error),
          assetClassId: variables.params.assetClassId,
        });
        // No cache rollback — bounding-box params make snapshot key unreliable.
        // Command bus executions are not rolled back by cache rollback — by design.
        if (variables.data.id) {
          failWorkSubmissionsForHost(
            variables.data.id,
            error instanceof Error ? error.message : String(error)
          );
        }
      },
      onSettled: (
        _data: void | CreateAssetResponse,
        _error: unknown,
        variables: { params: { assetClassId: Tp3Id }; data: CreateAsset } | undefined
      ): void => {
        if (!variables) return;
        const currentTenantPid = TenantRepository.getCurrentTenantPid();
        queryClient.invalidateQueries({
          queryKey: getGetAssetsQueryKey(currentTenantPid, {
            assetClassId: variables.params.assetClassId,
          }),
          exact: false,
        });
      },
    };
  },

  useUpdateAsset() {
    return useApiClientUpdateAsset({
      mutation: {
        ...AssetRepository.getUpdateAssetMutationConfig(),
      },
    });
  },

  getUpdateAssetMutationConfig(): MutationOptions {
    const { onMutate, onError, onSuccess, onSettled } =
      AssetRepository._updateCallbacks(moduleQueryClient);

    return getUpdateAssetMutationOptions({
      mutation: {
        ...defaultMutationOptions,
        onMutate,
        onSuccess,
        onError,
        onSettled,
        mutationKey: ['updateAsset'],
      },
    }) as MutationOptions;
  },

  _updateCallbacks(queryClient: QueryClient) {
    type UpdateAssetContext = {
      previousAsset?: Asset;
      assetDetailKeys: readonly (readonly unknown[])[];
    };

    return {
      onMutate: async ({
        params: { assetClassId },
        assetId,
        data,
      }: {
        params: { assetClassId: Tp3Id };
        assetId: Tp3Id;
        data: UpdateAsset;
      }): Promise<UpdateAssetContext> => {
        const tenantId = TenantRepository.getCurrentTenantPid();
        const assetsQueryKey = getGetAssetsQueryKey(tenantId, { assetClassId });

        await queryClient.cancelQueries({
          queryKey: assetsQueryKey,
          exact: false,
        });

        commandBus.execute({
          payload: new UpdateAssetCommand({
            id: assetId,
            assetClassId: assetClassId,
            tp2AssetId: data.tp2AssetId,
            attributes: data.attributes,
          }),
          type: 'UpdateAssetCommand',
        });
        // Note: Command bus execution is not rolled back by cache rollback — by design.

        // Compute both key shapes: generic (no tp2AssetId) and tp2-scoped (with tp2AssetId).
        // Both may be populated in the cache depending on how the asset was last fetched.
        const genericKey = getGetAssetQueryKey(tenantId, { assetClassId, assetId });
        const assetDetailKeys: (readonly unknown[])[] = [genericKey];
        if (data.tp2AssetId) {
          assetDetailKeys.push(
            getGetAssetQueryKey(tenantId, { assetClassId, assetId, tp2AssetId: data.tp2AssetId })
          );
        }

        const previousAsset =
          queryClient.getQueryData<Asset>(genericKey) ??
          (data.tp2AssetId
            ? queryClient.getQueryData<Asset>(
                getGetAssetQueryKey(tenantId, {
                  assetClassId,
                  assetId,
                  tp2AssetId: data.tp2AssetId,
                })
              )
            : undefined);

        return { previousAsset, assetDetailKeys };
      },
      onSuccess: async (
        _data: void,
        variables: {
          params: { assetClassId: Tp3Id };
          assetId: Tp3Id;
          data: UpdateAsset;
        }
      ): Promise<void> => {
        // No need to refetch data, the data is updated in the event handler.
        // The assetId doubles as the work-outbox submissionId for the edit-tree flow, so
        // this (persisted) success unblocks work queued against this save. No-op if none.
        markSubmissionHostDone(variables.assetId, {});
      },
      onError: (
        error: Error | AxiosError,
        variables: {
          params: { assetClassId: Tp3Id };
          assetId: Tp3Id;
          data: UpdateAsset;
        },
        context: UpdateAssetContext | undefined
      ): void => {
        if (context?.previousAsset) {
          for (const key of context.assetDetailKeys) {
            queryClient.setQueryData(key, context.previousAsset);
          }
        }
        loggerService.log(LoggerLevel.ERROR, 'Update asset error', {
          error: error instanceof Error ? error.message : String(error),
        });
        failWorkSubmissionsForHost(
          variables.assetId,
          error instanceof Error ? error.message : String(error)
        );
      },
      onSettled: (
        _data: unknown,
        _error: unknown,
        variables:
          | {
              params: { assetClassId: Tp3Id };
              assetId: Tp3Id;
              data: UpdateAsset;
            }
          | undefined
      ): void => {
        if (!variables) return;
        const tenantId = TenantRepository.getCurrentTenantPid();
        const { assetClassId } = variables.params;
        const { assetId } = variables;

        // Invalidate both key shapes so neither generic nor tp2-scoped detail stays stale.
        queryClient.invalidateQueries({
          queryKey: getGetAssetQueryKey(tenantId, { assetClassId, assetId }),
        });
        if (variables.data.tp2AssetId) {
          queryClient.invalidateQueries({
            queryKey: getGetAssetQueryKey(tenantId, {
              assetClassId,
              assetId,
              tp2AssetId: variables.data.tp2AssetId,
            }),
          });
        }
        queryClient.invalidateQueries({
          queryKey: getGetAssetsQueryKey(tenantId, { assetClassId }),
          exact: false,
        });
      },
    };
  },

  useAsset(assetClassId: Tp3Id, asset: AssetWithTp2) {
    const { fieldEntities } = FieldRepository.useGetFields(assetClassId);
    const tenantId = TenantRepository.getCurrentTenantPid();

    const useQueryResponse = useGetAsset(
      tenantId,
      { assetId: asset.id, assetClassId, tp2AssetId: asset.tp2AssetId },
      {
        query: {
          gcTime: 0,
        },
      }
    );

    const assetEntity = useMemo(() => {
      if (!useQueryResponse?.data) {
        return null;
      }
      return AssetMapper.fromDTO(useQueryResponse.data, fieldEntities);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(useQueryResponse?.data)]);

    const { tenant } = TenantRepository.useCurrentTenant();
    const { assetClass } = AssetClassRepository.useCurrentAssetClass(tenant?.id);
    if (useQueryResponse?.failureReason && assetClass?.id) {
      const axiosResponse = useQueryResponse?.failureReason as AxiosError;
      const status = axiosResponse.response?.status;
      if (status === 404) {
        this.refetchAsset(moduleQueryClient, assetClass.id, asset).catch((err) => {
          loggerService.log(LoggerLevel.ERROR, err);
        });
        return {
          ...useQueryResponse,
          assetEntity: null,
        };
      }
    }

    return {
      ...useQueryResponse,
      assetEntity,
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterAssetData(data: Record<string, any>, fieldEntities: FieldEntity[]): Record<string, any> {
    // Resolved once per snapshot rather than per field: the aggregate's exclusion depends on
    // the asset class and the flag, not on the field alone.
    const stemEditorLive =
      isFeatureFlagEnabled(FLAG_MULTI_STEM) && isStemEligible(resolveStemFieldSet(fieldEntities));

    return Object.keys(data).reduce((acc, key) => {
      const field = FieldEntity.findFieldFromId(fieldEntities, key);
      if (!shouldKeepField(field, stemEditorLive)) {
        return acc;
      }
      acc[key] = data[key];
      return acc;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as Record<string, any>);
  },

  setLastLoadedAsset(
    assetId: Tp3Id,
    tp2AssetId: number | undefined,
    assetClassId: Tp3Id,
    fieldEntities: FieldEntity[],
    attributes: AssetAttributes
  ) {
    const tenant = TenantRepository.getCurrentTenantPid();
    const filteredData = this.filterAssetData(attributes, fieldEntities);
    const queryInfo = {
      assetClassId,
      assetId: assetId,
      tp2AssetId: tp2AssetId,
      data: filteredData,
    };
    dispatch(saveLastLoadedAsset({ tenant, asset: queryInfo }));
  },

  getLastLoadedAsset() {
    const tenant = TenantRepository.getCurrentTenantPid();
    const { assets } = store.getState();
    return assets.lastLoadedAssetByTenant[tenant] || null;
  },

  /**
   * Records the most recently accessed asset for the current tenant so the map
   * can highlight it. Called when a tree's details are opened (regardless of
   * whether the user saves or changes anything), so the highlight always tracks
   * the last tree the user actually looked at.
   */
  setLastAccessedAsset(assetId: Tp3Id) {
    const tenant = TenantRepository.getCurrentTenantPid();
    dispatch(setLastAccessedAsset({ tenant, assetId }));
  },

  getLastAccessedAssetId() {
    const tenant = TenantRepository.getCurrentTenantPid();
    const { assets } = store.getState();
    return assets.lastAccessedAssetIdByTenant[tenant] || null;
  },

  clearLastLoadedAsset() {
    const tenant = TenantRepository.getCurrentTenantPid();
    dispatch(clearLastLoadedAsset(tenant));
  },

  async getAsset(assetClassId: Tp3Id, asset: AssetWithTp2, fields: FieldEntity[]) {
    const tenantId = TenantRepository.getCurrentTenantPid();

    const queryKey = getGetAssetQueryKey(tenantId, { assetId: asset.id, assetClassId });
    const assetDto = await moduleQueryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getAssetFromApi(tenantId, {
          assetId: asset.id,
          assetClassId,
          tp2AssetId: asset.tp2AssetId,
        }),
    });

    return AssetMapper.fromDTO(assetDto, fields);
  },

  async refetchAsset(queryClient: QueryClient, assetClassId: Tp3Id, asset: AssetWithTp2) {
    const tenantId = TenantRepository.getCurrentTenantPid();

    const getAssetQueryKey = getGetAssetQueryKey(tenantId, { assetId: asset.id, assetClassId });
    await queryClient.invalidateQueries({ queryKey: getAssetQueryKey });
  },

  async refetchAssets(assetClassId: string) {
    const tenantId = TenantRepository.getCurrentTenantPid();

    await moduleQueryClient.refetchQueries({
      queryKey: getGetAssetsQueryKey(tenantId, {
        assetClassId,
      }),
      exact: false,
    });
  },

  // Photo upload status methods
  hasPendingPhotoUploads(): boolean {
    const { assets } = store.getState();
    return assets.hasPendingPhotoUploads || assets.hasActiveCompressions;
  },

  setHasPendingPhotoUploads(hasPending: boolean) {
    dispatch(setHasPendingPhotoUploads(hasPending));
  },

  hasActiveCompressions(): boolean {
    const { assets } = store.getState();
    return assets.hasActiveCompressions;
  },

  setHasActiveCompressions(hasCompressions: boolean) {
    dispatch(setHasActiveCompressions(hasCompressions));
  },

  cancelPendingUploads() {
    try {
      // Request cancellation via Redux
      dispatch(setCancelUploadsRequested(true));

      loggerService.log(LoggerLevel.DEBUG, 'Upload cancellation requested via Redux');

      // Only reset the flag after successful cancellation request
      this.setHasPendingPhotoUploads(false);
    } catch (error) {
      loggerService.log(LoggerLevel.WARN, 'Failed to request upload cancellation', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      // Don't reset the flag if cancellation request failed
      // Uploads are likely still active
      loggerService.log(
        LoggerLevel.WARN,
        'Keeping hasPendingPhotoUploads=true due to failed cancellation request'
      );
    }
  },

  clearCancelRequest() {
    dispatch(setCancelUploadsRequested(false));
  },

  getActiveQueryFromNonExactQueryKey(queryKey: readonly unknown[]) {
    const activeQuery = moduleQueryClient.getQueryCache().find({
      queryKey,
      exact: false,
      predicate: (query) => {
        const { queryKey } = query;
        const params = queryKey[5] as GetAssetsParams | undefined;

        return params?.minLng !== undefined;
      },
    });

    return activeQuery ? activeQuery.queryKey : queryKey;
  },

  async addAssetToLocalState(asset: AssetEntity, refetch = true) {
    const assetDto = AssetMapper.toDTO(asset);
    const currentTenantPid = TenantRepository.getCurrentTenantPid();
    // Cancel any outgoing refetches (so they don't overwrite our update)
    await moduleQueryClient.cancelQueries({
      queryKey: getGetTenantByIdQueryKey({ tenantId: currentTenantPid }),
    });

    // Asset Update
    const assetQueryKey = getGetAssetQueryKey(currentTenantPid, {
      assetId: asset.id,
      assetClassId: asset.assetClassId,
    });
    await moduleQueryClient.cancelQueries({
      queryKey: assetQueryKey,
    });
    moduleQueryClient.setQueryData(assetQueryKey, (): Asset => assetDto);

    // Assets Update
    const assetsQueryKey = getGetAssetsQueryKey(currentTenantPid, {
      assetClassId: asset.assetClassId,
    });
    await moduleQueryClient.cancelQueries({
      queryKey: assetsQueryKey,
      exact: false,
    });

    const activeAssetsQueryKey = this.getActiveQueryFromNonExactQueryKey(assetsQueryKey);

    moduleQueryClient.setQueryData(activeAssetsQueryKey, (old: Asset[] | undefined) => [
      ...(old || []),
      assetDto,
    ]);

    if (refetch) {
      moduleQueryClient.invalidateQueries({
        queryKey: assetQueryKey,
      });

      moduleQueryClient.invalidateQueries({
        queryKey: activeAssetsQueryKey,
      });
    }
  },

  async updateAssetLocalState(asset: AssetEntity, refetch = true) {
    const assetDto = AssetMapper.toDTO(asset);

    const currentTenantPid = TenantRepository.getCurrentTenantPid();
    // Cancel any outgoing refetches (so they don't overwrite our update)
    await moduleQueryClient.cancelQueries({
      queryKey: getGetTenantByIdQueryKey({ tenantId: currentTenantPid }),
    });

    // Asset Update
    const assetQueryKey = getGetAssetQueryKey(currentTenantPid, {
      assetId: asset.id,
      assetClassId: asset.assetClassId,
    });
    await moduleQueryClient.cancelQueries({
      queryKey: assetQueryKey,
    });
    moduleQueryClient.setQueryData(assetQueryKey, (): Asset => assetDto);

    // Assets Update
    const assetsQueryKey = getGetAssetsQueryKey(currentTenantPid, {
      assetClassId: asset.assetClassId,
    });
    await moduleQueryClient.cancelQueries({
      queryKey: assetsQueryKey,
      exact: false,
    });

    const activeAssetsQueryKey = this.getActiveQueryFromNonExactQueryKey(assetsQueryKey);

    moduleQueryClient.setQueryData(activeAssetsQueryKey, (oldAssetDTOs: Asset[] | undefined) =>
      upsertAssetDto(oldAssetDTOs, assetDto)
    );

    if (refetch) {
      moduleQueryClient.invalidateQueries({
        queryKey: assetQueryKey,
      });

      moduleQueryClient.invalidateQueries({
        queryKey: activeAssetsQueryKey,
      });
    }
  },

  /**
   * Fetch assets by TP2 work task IDs with optional bounding box.
   * React Query hook version for use in components.
   *
   * @param assetClassId - Asset class ID
   * @param tp2WorkTaskIds - Array of TP2 work task IDs to fetch associated assets for
   * @param boundingBox - Optional bounding box to filter assets by viewport
   * @returns React Query result with assetEntities and boundingBox
   */
  useAssetsByWorkTaskIds(
    assetClassId: string,
    tp2WorkTaskIds: number[],
    boundingBox?: BoundingBox,
    options: { enabled?: boolean; dbhMin?: number; dbhMax?: number } = {}
  ) {
    const { enabled = true, dbhMin, dbhMax } = options;
    const { fieldEntities } = FieldRepository.useGetFields(assetClassId);
    const tenantId = TenantRepository.getCurrentTenantPid();
    const { auth } = store.getState();

    // Stabilize tp2WorkTaskIds array to prevent unnecessary recomputes when
    // callers pass new array references with the same values
    const tp2WorkTaskIdsKey = tp2WorkTaskIds.join(',');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableTp2WorkTaskIds = useMemo(() => tp2WorkTaskIds, [tp2WorkTaskIdsKey]);

    const requestBody: GetAssetsFromWorkTaskIdsRequest = useMemo(
      () => ({
        tp2WorkTaskIds: stableTp2WorkTaskIds,
        boundingBox: boundingBox
          ? {
              minLng: boundingBox.minLng,
              minLat: boundingBox.minLat,
              maxLng: boundingBox.maxLng,
              maxLat: boundingBox.maxLat,
            }
          : undefined,
        dbhMin,
        dbhMax,
      }),
      [stableTp2WorkTaskIds, boundingBox, dbhMin, dbhMax]
    );

    const params: GetAssetsFromWorkTaskIdsParams = useMemo(
      () => ({
        assetClassId,
      }),
      [assetClassId]
    );

    const useQueryResponse = useQuery({
      queryKey: ['getAssetsFromWorkTaskIds', tenantId, params, requestBody],
      queryFn: async ({ signal }) => {
        if (!tenantId) {
          throw new Error('Tenant ID is required');
        }
        return getAssetsFromWorkTaskIds(tenantId, requestBody, params, signal);
      },
      enabled:
        enabled &&
        !!auth.isAuthenticated &&
        !!auth.user &&
        !!tenantId &&
        !!assetClassId &&
        stableTp2WorkTaskIds.length > 0,
    });

    const assetEntities = useMemo(() => {
      const response = useQueryResponse?.data as GetAssetsFromWorkTaskIdsResponse | undefined;
      if (!response?.assets || !Array.isArray(response.assets)) {
        return [];
      }
      return response.assets.map((asset: Asset) => AssetMapper.fromDTO(asset, fieldEntities));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(useQueryResponse?.data), fieldEntities]);

    const responseBoundingBox = useMemo(() => {
      const response = useQueryResponse?.data as GetAssetsFromWorkTaskIdsResponse | undefined;
      if (!response?.boundingBox) {
        return null;
      }
      return {
        minLng: response.boundingBox.minLng,
        minLat: response.boundingBox.minLat,
        maxLng: response.boundingBox.maxLng,
        maxLat: response.boundingBox.maxLat,
      };
    }, [useQueryResponse?.data]);

    return {
      ...useQueryResponse,
      assetEntities,
      boundingBox: responseBoundingBox,
    };
  },

  /**
   * Fetch assets by TP2 work task IDs with optional bounding box.
   * Imperative version for use in navigation flows.
   *
   * @param assetClassId - Asset class ID
   * @param tp2WorkTaskIds - Array of TP2 work task IDs to fetch associated assets for
   * @param boundingBox - Optional bounding box to filter assets by viewport
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to object with assets and boundingBox
   */
  async getAssetsByWorkTaskIds(
    assetClassId: string,
    tp2WorkTaskIds: number[],
    boundingBox?: BoundingBox,
    signal?: AbortSignal
  ): Promise<{
    assets: AssetEntity[];
    boundingBox: BoundingBox | null;
  }> {
    const tenantId = TenantRepository.getCurrentTenantPid();
    if (!tenantId || !assetClassId || !tp2WorkTaskIds || tp2WorkTaskIds.length === 0) {
      return { assets: [], boundingBox: null };
    }

    const requestBody: GetAssetsFromWorkTaskIdsRequest = {
      tp2WorkTaskIds,
      boundingBox: boundingBox
        ? {
            minLng: boundingBox.minLng,
            minLat: boundingBox.minLat,
            maxLng: boundingBox.maxLng,
            maxLat: boundingBox.maxLat,
          }
        : undefined,
    };

    const params: GetAssetsFromWorkTaskIdsParams = {
      assetClassId,
    };

    const response = await getAssetsFromWorkTaskIds(tenantId, requestBody, params, signal);

    if (!response?.assets || !Array.isArray(response.assets)) {
      return { assets: [], boundingBox: null };
    }

    const fieldEntities = await FieldRepository.getFields(assetClassId);
    const assets = response.assets.map((asset: Asset) => AssetMapper.fromDTO(asset, fieldEntities));

    const responseBoundingBox = response.boundingBox
      ? {
          minLng: response.boundingBox.minLng,
          minLat: response.boundingBox.minLat,
          maxLng: response.boundingBox.maxLng,
          maxLat: response.boundingBox.maxLat,
        }
      : null;

    return {
      assets,
      boundingBox: responseBoundingBox,
    };
  },
};

export default AssetRepository;
